import React from 'react';
import { useLocalizedText } from '../../hooks/localizedText';
import { Container, Spinner } from '@amzn/awsui-components-react';
import { Event, GetEventDetailsByEventIdCommandOutput } from '@amzn/red-velvet-api';
import { useAllPagesRedVelvetQuery } from '../../hooks/allPagesRedVelvetQuery';
import { EventTitleHeader } from '../events/eventTitleHeader';
import { EventCard } from '../attendance/coordinator/eventCard';
import {
  DISPLAY_EVENT_ATTRIBUTES_TYPE,
  DISPLAY_EVENT_COORDINATOR_INFO,
  DISPLAY_EVENT_DATE_INFO,
  DISPLAY_EVENT_LOCATION_INFO,
  DISPLAY_EVENT_ORGANIZATION_INFO,
  DISPLAY_SHIFTS_COUNT_INFO,
  DISPLAY_TOTAL_SIGNUPS_INFO,
} from '../events/eventInfo';

interface WaitlistEventCardProps {
  event: Event;
}

const DISPLAY_ATTRIBUTES: DISPLAY_EVENT_ATTRIBUTES_TYPE[] = [
  DISPLAY_EVENT_ORGANIZATION_INFO,
  DISPLAY_EVENT_COORDINATOR_INFO,
  DISPLAY_EVENT_LOCATION_INFO,
  DISPLAY_EVENT_DATE_INFO,
  DISPLAY_SHIFTS_COUNT_INFO,
  DISPLAY_TOTAL_SIGNUPS_INFO,
];

/**
 * Event header card for the coordinator Manage Waitlist page. Fetches detailedShifts via
 * getEventDetailsByEventId to accurately compute shift count and total signups (handles
 * unlimited-capacity shifts correctly). Reuses EventTitleHeader + EventCard.
 */
export function WaitlistEventCard({ event }: WaitlistEventCardProps) {
  const { t } = useLocalizedText(['attendance', 'translation']);

  const { data: detailedShifts, isPending } = useAllPagesRedVelvetQuery({
    enabled: !!event.eventId,
    redVelvetQueryKey: ['getEventDetailsByEventId', { eventId: event.eventId, pageSize: 25 }],
    converter: (output: GetEventDetailsByEventIdCommandOutput) => ({
      items: output.detailedShifts ?? [],
      next: output.page,
    }),
  });

  if (isPending) {
    return (
      <Container>
        <Spinner />
        <span>{t('fullEvent.loadingEvent')}</span>
      </Container>
    );
  }

  return (
    <Container>
      <EventTitleHeader
        event={event}
        editSource="manageWaitlist"
        titleAnalyticsName="manageWaitlist_title"
      />
      <EventCard
        event={event}
        detailedShifts={detailedShifts}
        displayAttributes={DISPLAY_ATTRIBUTES}
      />
    </Container>
  );
}
